import { ChangeDetectorRef, Component, inject, ChangeDetectionStrategy } from '@angular/core';
import type { FormControl, FormGroup } from '@angular/forms';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject, take, takeUntil } from 'rxjs';

// eslint-disable-next-line boundaries/element-types -- FIXME
import { injectCountryList } from '@core/country-data';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { Country } from '@core/locations-types';
// eslint-disable-next-line boundaries/element-types -- FIXME
import type { CompanyVerificationRequestPayload } from '@core/spam-verification-data';
// eslint-disable-next-line boundaries/element-types -- FIXME
import { SpamVerificationActions } from '@core/spam-verification-data';
import type { DialogComponentI, SelectOption } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  FormFieldComponent,
  IconHelpComponent,
  InputComponent,
  LabelComponent,
  NotificationsController,
  RtTooltip,
  SelectComponent,
} from '@core/user-interface-legacy';

@Component({
  selector: 'rt-spam-verification-dialog',
  templateUrl: './spam-verification-dialog.component.html',
  styleUrls: ['./spam-verification-dialog.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonComponent,
    ButtonLoadingDirective,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    FormFieldComponent,
    IconHelpComponent,
    InputComponent,
    LabelComponent,
    RtTooltip,
    SelectComponent,
    ReactiveFormsModule,
    TranslatePipe,
  ],
})
export class SpamVerificationDialogComponent implements DialogComponentI<any, any> {
  private readonly formBuilder = inject(FormBuilder);
  private readonly dialogRef = inject(DialogRef);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly notifications = inject(NotificationsController);
  private readonly i18n = inject(I18nService);

  public form: FormGroup<{
    companyName: FormControl<string>;
    companyWebsite: FormControl<string>;
    companyAddress: FormControl<string>;
    companyTaxNumber: FormControl<string>;
    companyCountry: FormControl<Country['name']>;
  }>;

  public loading: boolean = false;
  public countries: Array<SelectOption<Country['name']>> = injectCountryList().map(country => ({
    label: country.name,
    value: country.name,
  }));

  private ngOnDestroy$: Subject<void> = new Subject<void>();

  public ngOnInit(): void {
    this.form = this.formBuilder.nonNullable.group({
      companyName: ['', [Validators.required]],
      companyWebsite: ['', [Validators.required]],
      companyAddress: ['', [Validators.required]],
      companyTaxNumber: ['', [Validators.required]],
      companyCountry: ['', [Validators.required]],
    });

    this.form.valueChanges.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
      this.cdRef.detectChanges();
    });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public setDialogData(data: any, triggerUpdate: boolean): void {
    //
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  public save(): void {
    this.loading = true;
    this.cdRef.detectChanges();

    this.store.dispatch(SpamVerificationActions.update({ payload: this.concatPayload() }));

    this.actions$
      .pipe(ofType(SpamVerificationActions.updateSuccess, SpamVerificationActions.updateFailed))
      .pipe(take(1), takeUntil(this.ngOnDestroy$))
      .subscribe(() => {
        this.loading = false;
        this.cdRef.detectChanges();
      });

    this.actions$
      .pipe(ofType(SpamVerificationActions.updateSuccess))
      .pipe(take(1), takeUntil(this.ngOnDestroy$))
      .subscribe(() => {
        this.notifications.success(
          this.i18n.translate('app.spam_verification.dialog.success_toast'),
        );
        this.dialogRef.close();
      });
  }

  private concatPayload(): CompanyVerificationRequestPayload {
    const { companyAddress, companyCountry, ...rest } = this.form.value;

    return <CompanyVerificationRequestPayload>{
      ...rest,
      companyAddress: `${companyAddress}, ${companyCountry}`,
    };
  }
}
